/**
 * Recruitment Funnel Widget (Cumulative — B2B + B2C)
 *
 * 6-stage funnel (Leads → Applications → Offers → Accepts → Deposit Paid → Enrols)
 * for a given intake, scoped to the current user's own pipeline by default.
 * Cards are clickable and deep-link to the relevant admin tab pre-filtered
 * by journey_stage. Enrol target for September 2026 = 85.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Loader2, TrendingUp, TrendingDown, Users, FileText, Award,
  CheckCircle2, DollarSign, GraduationCap, ChevronRight, MessageCircle,
} from 'lucide-react';
import ChaseOffersDialog from '@/components/admin/ChaseOffersDialog';
import IntakeUploadDialog from '@/components/admin/IntakeUploadDialog';

const STAGE_ICONS = {
  leads: Users,
  applications: FileText,
  offers: Award,
  accepts: CheckCircle2,
  payments: DollarSign,
  enrols: GraduationCap,
};

export default function B2CFunnelWidget({
  isAdmin = false,
  defaultIntake = 'September 2026',
}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(isAdmin ? 'all' : 'mine');
  const [intake, setIntake] = useState(defaultIntake);
  const [mode, setMode] = useState('hybrid'); // 'hybrid' | 'primary'
  const [chaseOpen, setChaseOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/analytics/b2c-funnel', { params: { intake, scope, vertical: 'all', mode } })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [intake, scope, mode, refreshKey]);

  const goToStage = (stage) => {
    if (!stage?.nav_tab) return;
    const params = new URLSearchParams();
    params.set('tab', stage.nav_tab);
    if (stage.nav_stage) params.set('stage', stage.nav_stage);
    params.set('intake', intake);
    navigate(`/admin?${params.toString()}`);
  };

  const totalLeads = data?.stages?.[0]?.count ?? 0;
  const enrolTarget = data?.enrol_target;

  return (
    <Card data-testid="b2c-funnel-widget" className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
            <TrendingUp className="w-5 h-5 text-teal-600" />
            Recruitment Funnel — <span className="text-teal-600">{intake}</span>
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
              {data?.vertical || 'All Verticals'} · {scope === 'mine' ? 'my pipeline' : 'company-wide'}
              {typeof totalLeads === 'number' ? ` · ${totalLeads} leads` : ''}
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <select
              value={intake}
              onChange={e => setIntake(e.target.value)}
              className="text-xs bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded px-2 py-1 text-gray-800 dark:text-white"
              data-testid="funnel-intake-select"
            >
              <option value="September 2026">September 2026</option>
              <option value="January 2027">January 2027</option>
              <option value="May 2027">May 2027</option>
              <option value="September 2027">September 2027</option>
              <option value="January 2028">January 2028</option>
            </select>
            {isAdmin && (
              <div className="inline-flex rounded-md border border-gray-200 dark:border-slate-600 overflow-hidden text-xs">
                <button
                  onClick={() => setScope('mine')}
                  className={`px-3 py-1 ${scope === 'mine' ? 'bg-teal-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300'}`}
                  data-testid="funnel-scope-mine"
                >Mine</button>
                <button
                  onClick={() => setScope('all')}
                  className={`px-3 py-1 ${scope === 'all' ? 'bg-teal-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300'}`}
                  data-testid="funnel-scope-all"
                >All</button>
              </div>
            )}
            <div className="inline-flex rounded-md border border-gray-200 dark:border-slate-600 overflow-hidden text-xs" title="Hybrid = students + apps subtext · Primary = one primary app per student">
              <button
                onClick={() => setMode('hybrid')}
                className={`px-3 py-1 ${mode === 'hybrid' ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300'}`}
                data-testid="funnel-mode-hybrid"
              >Hybrid</button>
              <button
                onClick={() => setMode('primary')}
                className={`px-3 py-1 ${mode === 'primary' ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300'}`}
                data-testid="funnel-mode-primary"
              >Primary only</button>
            </div>
            {isAdmin && (
              <button
                onClick={async () => {
                  const proceed = window.confirm(`Assign "${intake}" to all leads / students / applications currently missing an intake?\n\nThis makes untagged legacy records show up in the ${intake} funnel. Records already tagged with a different intake are left alone.`);
                  if (!proceed) return;
                  try {
                    const res = await api.post('/admin/intake-import/backfill-intake', null, { params: { target_intake: intake } });
                    setRefreshKey(k => k + 1);
                    const { toast } = await import('sonner');
                    const total = ['leads','students','applications'].reduce((n,k) => n + (res.data.counts?.[k]?.backfilled || 0), 0);
                    toast.success(`Backfilled ${total} records into ${intake}`);
                  } catch (e) {
                    const { toast } = await import('sonner');
                    toast.error(`Backfill failed: ${e.response?.data?.detail || e.message}`);
                  }
                }}
                className="text-xs px-3 py-1 rounded-md bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                data-testid="funnel-backfill-intake"
                title={`Set preferred_intake="${intake}" on records currently missing it`}
              >
                Backfill Intake
              </button>
            )}
            {isAdmin && (
              <button
                onClick={async () => {
                  try {
                    const res = await api.post('/admin/intake-import/normalize-primary', null, { params: { intake } });
                    setRefreshKey(k => k + 1);
                    const { toast } = await import('sonner');
                    toast.success(`Normalized ${res.data.students_healed} students · ${res.data.total_primary_apps} primary apps`);
                  } catch (e) {
                    const { toast } = await import('sonner');
                    toast.error(`Normalize failed: ${e.response?.data?.detail || e.message}`);
                  }
                }}
                className="text-xs px-3 py-1 rounded-md bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800"
                data-testid="funnel-normalize-primary"
                title="Force-recompute is_primary for the current intake"
              >
                Normalize Primary
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setUploadOpen(true)}
                className="text-xs px-3 py-1 rounded-md bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-1"
                data-testid="funnel-upload-intake"
                title="Upload xlsx intake sheet"
              >
                <span className="text-sm leading-none">+</span> Upload Intake
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading funnel...
          </div>
        ) : !data ? (
          <div className="py-8 text-center text-gray-500 text-sm">Funnel data unavailable.</div>
        ) : (
          <>
            {/* Funnel — single-colour clickable cards (Salesforce-style) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              {data.stages.map((s, i) => {
                const Icon = STAGE_ICONS[s.key] || Users;
                const prevStudents = i > 0 ? (data.stages[i - 1].students ?? data.stages[i - 1].count) : null;
                const dropPct = i > 0
                  ? Math.round(100 * (s.students ?? s.count) / Math.max(1, prevStudents))
                  : null;
                // On-track color: enrols compared to hard target, others to drop % vs KPI targets
                let onTrack = null;
                if (s.key === 'enrols' && enrolTarget) {
                  onTrack = (s.students ?? s.count) >= enrolTarget;
                }
                const numberClass = onTrack === false
                  ? 'text-rose-600 dark:text-rose-400'
                  : onTrack === true
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-teal-700 dark:text-teal-300';

                // Show apps subtext only when it differs from student count
                const hasAppSubtext = typeof s.apps_count === 'number' && s.apps_count !== s.students;

                return (
                  <div key={s.key} className="group relative">
                    <button
                      type="button"
                      onClick={() => goToStage(s)}
                      className="w-full text-left rounded-xl bg-white dark:bg-slate-900 border-2 border-teal-100 dark:border-teal-900/50 p-4 shadow-sm hover:shadow-lg hover:border-teal-500 dark:hover:border-teal-400 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500"
                      data-testid={`funnel-stage-${s.key}`}
                      aria-label={`View ${s.label} — ${s.students ?? s.count}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/40 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                          </div>
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                          {i === 0 ? 'START' : `${dropPct}%`}
                        </span>
                      </div>
                      <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">
                        {s.label}
                      </div>
                      <div className={`text-4xl font-black leading-none mt-2 ${numberClass}`}>
                        {s.students ?? s.count}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                        {s.key === 'leads' ? 'leads' : 'students'}
                        {hasAppSubtext && (
                          <> · <span className="font-semibold text-gray-700 dark:text-gray-300">{s.apps_count}</span> apps</>
                        )}
                      </div>
                      {s.target ? (
                        <div className="mt-3 pt-2 border-t border-gray-100 dark:border-slate-800 text-[11px] text-gray-600 dark:text-gray-400 font-medium">
                          Target: <span className="text-gray-900 dark:text-white">{s.target} Enrols</span>
                        </div>
                      ) : null}
                      <div className="mt-2 flex items-center gap-1 text-[10px] text-teal-600 dark:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity">
                        View list <ChevronRight className="w-3 h-3" />
                      </div>
                      {i < data.stages.length - 1 && (
                        <div className="hidden lg:block absolute -right-2 top-1/2 -translate-y-1/2 z-10 text-teal-400 text-xl font-bold pointer-events-none">›</div>
                      )}
                    </button>
                    {/* "Chase" action — only on Offers Made card when there are unaccepted offers */}
                    {s.key === 'offers' && (s.students ?? 0) > (data.stages.find(x => x.key === 'accepts')?.students ?? 0) && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setChaseOpen(true); }}
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-semibold shadow-md hover:shadow-lg transition-all whitespace-nowrap"
                        data-testid="funnel-chase-offers"
                        title="Send WhatsApp / email nudge to unaccepted students"
                      >
                        <MessageCircle className="w-3 h-3" />
                        Chase {(s.students ?? 0) - (data.stages.find(x => x.key === 'accepts')?.students ?? 0)}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* KPI compliance cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
              {data.kpis.map(k => (
                <div
                  key={k.key}
                  className={`p-3 rounded-lg border ${
                    k.on_track
                      ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                      : 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800'
                  }`}
                  data-testid={`kpi-${k.key}`}
                >
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{k.label}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className={`text-2xl font-bold ${
                      k.on_track ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'
                    }`}>{k.actual_pct}%</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">/ {k.target_pct}%</span>
                    {k.on_track
                      ? <TrendingUp className="w-4 h-4 text-emerald-600" />
                      : <TrendingDown className="w-4 h-4 text-rose-600" />}
                  </div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                    {k.note || `Target: ${k.target_pct}%`}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 text-[10px] text-gray-400 dark:text-gray-500">
              KPIs measured per applicant (unique students) · Cumulative (B2B + B2C) · Intake: {intake}
              {enrolTarget ? ` · Enrol target: ${enrolTarget} students` : ''}
            </div>
          </>
        )}
      </CardContent>
      <ChaseOffersDialog
        open={chaseOpen}
        onOpenChange={setChaseOpen}
        intake={intake}
        scope={scope}
      />
      <IntakeUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        defaultIntake={intake}
        onImported={(res) => {
          if (res?.intake_label) setIntake(res.intake_label);
          setRefreshKey(k => k + 1);
        }}
      />
    </Card>
  );
}
